import type {LocaleCode, LocaleDetectionResult, Messages} from '../types/i18n/core'
import {logClientWarn} from './client-logger'
import {isPublicTenantEntryPath} from './tenant-seo-policy'

import plMessages from '../../locales/pl'

export {plMessages}

const locales: LocaleCode[] = ['pl', 'en']

const defaultLocale: LocaleCode = 'pl'

function detectLocale(
    urlLocale?: string,
    storageLocale?: string,
    browserLocales?: readonly string[]
): LocaleDetectionResult {
    if (urlLocale && isValidLocale(urlLocale)) {
        return {
            locale: urlLocale as LocaleCode,
            source: 'url',
            confidence: 'high'
        }
    }

    if (storageLocale && isValidLocale(storageLocale)) {
        return {
            locale: storageLocale as LocaleCode,
            source: 'localStorage',
            confidence: 'high'
        }
    }

    if (browserLocales && browserLocales.length > 0) {
        for (const browserLocale of browserLocales) {
            const langCode = browserLocale.toLowerCase().split('-')[0]
            if (langCode && isValidLocale(langCode)) {
                return {
                    locale: langCode as LocaleCode,
                    source: 'browser',
                    confidence: 'medium'
                }
            }
        }
    }

    return {
        locale: defaultLocale,
        source: 'default',
        confidence: 'low'
    }
}

export function isValidLocale(locale: string): locale is LocaleCode {
    return locales.includes(locale as LocaleCode)
}

export function getBrowserLocale(): LocaleDetectionResult {
    if (typeof window === 'undefined') {
        return {locale: defaultLocale, source: 'default', confidence: 'low'}
    }

    const storedLocale = localStorage.getItem('fitro-locale')
    // A studio's public pages are Polish content, so without a stored choice they stay Polish, also for crawlers.
    if (!storedLocale && isPublicTenantEntryPath(window.location.pathname)) {
        return {locale: defaultLocale, source: 'default', confidence: 'low'}
    }

    const browserLocales = navigator.languages || [navigator.language]

    return detectLocale(undefined, storedLocale || undefined, browserLocales)
}

export function storeLocalePreference(locale: LocaleCode): void {
    if (typeof window !== 'undefined') {
        localStorage.setItem('fitro-locale', locale)
        document.cookie = `fitro-locale=${locale}; path=/; max-age=31536000; samesite=lax`
    }
}

export async function loadMessages(locale: LocaleCode): Promise<Messages> {
    const base = plMessages as unknown as Messages
    if (locale === 'pl') return base
    if (locale === 'en') {
        const overlay = (await import('../../locales/en')).default as Record<string, any>
        const merged: Record<string, any> = {...(base as any)}
        for (const [domain, value] of Object.entries(overlay)) {
            if (
                Object.prototype.hasOwnProperty.call(base, domain) &&
                typeof (base as any)[domain] === 'object' &&
                typeof value === 'object'
            ) {
                merged[domain] = {...(base as any)[domain], ...value}
            } else {
                merged[domain] = value
            }
        }
        return merged as Messages
    }
    logClientWarn({
        event: 'i18n_unsupported_locale',
        source: 'i18n',
        message: `Unsupported locale '${locale}', falling back to '${defaultLocale}'.`,
        context: {
            locale,
            defaultLocale,
        },
    })
    return base as Messages
}


export function getLocaleConfig(code: LocaleCode) {
    const SUPPORTED_LOCALES = [
        {
            code: 'pl' as const,
            name: 'Polski',
            nativeName: 'Polski',
            flag: '🇵🇱',
            direction: 'ltr' as const,
            isDefault: true
        },
        {
            code: 'en' as const,
            name: 'English',
            nativeName: 'English',
            flag: '🇬🇧',
            direction: 'ltr' as const,
            isDefault: false
        }
    ]

    return SUPPORTED_LOCALES.find(locale => locale.code === code)
}

export const formatters = {
    number: (num: number, locale: LocaleCode = defaultLocale, options?: Intl.NumberFormatOptions) => {
        return new Intl.NumberFormat(locale, options).format(num)
    },

    currency: (amount: number, locale: LocaleCode = defaultLocale, currency: string = 'PLN') => {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency
        }).formatToParts(amount).map(part => part.type === 'currency' && currency === 'PLN' ? 'zł' : part.value).join('')
    }
}
